import React, { useState, useRef } from 'react';
import {
  Box,
  Flex,
  Text,
  Avatar,
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuDivider,
  IconButton,
  Portal,
  useToast,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useColorModeValue,
  VStack,
  Badge,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import { FiEdit2, FiMoreVertical } from 'react-icons/fi';
import { LuClock, LuTrash2, LuCheck, LuCircleCheck, LuPause, LuPlay, LuTriangleAlert, LuChevronDown } from 'react-icons/lu';
import { motion, AnimatePresence } from 'framer-motion';
import ScheduleTimelineDetails from './ScheduleTimelineDetails';
import useCountdown from '../hooks/useCountdown';
import { useActivityData } from '../hooks/useActivityData';
import { deleteSchedule, completeSchedule, pauseSchedule, resumeSchedule } from '../services/scheduleService';
import { useDeleteSchedule } from '../mutations/useDeleteSchedule';
import "../rainbow.css";
import { fetchWithAuth } from '../apiClient';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { useTrackEvent } from '../analytics';
import { getTaskHealthDisplay } from './utils/analyticsDisplay';
dayjs.extend(calendar);

const MotionFlex = motion(Flex);
const CONTROL_ACTIONS = new Set(['pause', 'resume', 'complete']);

const ScheduleItem = ({ item, refreshSchedules, initialExpanded = false, onEditIntent, editingTarget, completingTarget, automationDisabled = false, recoveryMessage = 'Ekkos is in recovery mode.' }) => {
  const toast = useToast();
  const track = useTrackEvent();
  const countdown = useCountdown(item.nextRunTime);
  const {
    activityData,
    loading,
    loaded,
    getConsecutiveRetryCount,
    fetchActivityData,
    isPaused: ledgerPaused,
    latestActivity,
  } = useActivityData(item.originJobKey, toast);
  const [expanded, setExpanded] = useState(initialExpanded);
  const { isOpen, onOpen, onClose } = useDisclosure();
  // menu disclosure so we can programmatically close it when actions are taken
  const { isOpen: menuOpen, onOpen: openMenu, onClose: closeMenu } = useDisclosure();
  const cancelRef = useRef();
  const [deletingJob, setDeletingJob] = useState(null);
  const [firingStatus, setFiringStatus] = useState(!!item.isFiring && !item.isPaused); // Initialize from server prop when present

  // Update firingStatus if parent passes a new item
  React.useEffect(() => {
    setFiringStatus(!!item.isFiring && !item.isPaused);
  }, [item.isFiring, item.isPaused]);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);

  // menu/theme colors (computed once to keep menu consistent with app theme)
  const menuBg = 'bg.surface';
  const menuColor = 'text.primary';
  const menuBorder = 'border.subtle';
  const menuShadow = useColorModeValue('0 8px 24px rgba(31,24,13,0.08)', '0 10px 30px rgba(0,0,0,0.6)');
  const menuItemHover = useColorModeValue('rgba(0,0,0,0.04)', 'rgba(255,255,255,0.02)');

  const modalContentBg = 'bg.surface';
  const modalTextColor = 'text.primary';
  const metadataColor = 'text.muted';
  const dialogBoxBg = useColorModeValue('bg.subtle', 'bg.elevated');
  const dialogMuted = 'text.muted';

  // Removed polling useEffect for /api/activity/check/{jobKey}

  const deleteMutation = useDeleteSchedule();

  const refreshAfterMutation = async () => {
    if (typeof refreshSchedules === 'function') {
      await refreshSchedules();
    }
    await fetchActivityData({ force: true });
  };

  const handleDelete = async () => {
    setDeletingJob(item.originJobKey);
    try {
      await deleteMutation.mutateAsync(item.originJobKey);
      track('schedule_delete', { source: 'timeline' });
      toast({ title: 'Reminder removed.', status: 'success', duration: 3000, isClosable: true });
    } catch (e) {
      track('schedule_delete_error', { source: 'timeline' });
      toast({ title: 'Error deleting schedule.', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setDeletingJob(null);
      onClose();
    }
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      track('schedule_complete_click', { source: 'timeline' });
      await completeSchedule(item.originJobKey, toast, refreshAfterMutation);
      track('schedule_complete_success', { source: 'timeline' });
    } catch (error) {
      track('schedule_complete_error', { source: 'timeline' });
      console.error('Complete schedule failed', error);
    } finally {
      setIsCompleting(false);
    }
  };

  const handlePause = async () => {
    setIsPausing(true);
    try {
      track('schedule_pause', { source: 'timeline' });
      await pauseSchedule(item.originJobKey, toast, refreshAfterMutation);
    } catch (error) {
      track('schedule_pause_error', { source: 'timeline' });
      console.error('Pause schedule failed', error);
    } finally {
      setIsPausing(false);
    }
  };

  const handleResume = async () => {
    setIsResuming(true);
    try {
      track('schedule_resume', { source: 'timeline' });
      await resumeSchedule(item.originJobKey, toast, refreshAfterMutation);
    } catch (error) {
      track('schedule_resume_error', { source: 'timeline' });
      console.error('Resume schedule failed', error);
    } finally {
      setIsResuming(false);
    }
  };

  const toggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  React.useEffect(() => {
    if (expanded && !loaded && !loading) {
      fetchActivityData();
    }
  }, [expanded, loaded, loading, fetchActivityData]);

  const handleEditIntent = (e) => {
    e?.stopPropagation?.();
    if (automationDisabled) {
      toast({
        title: 'Scheduling paused',
        description: recoveryMessage,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    track('schedule_edit_intent', { source: 'timeline' });
    if (typeof onEditIntent === 'function') onEditIntent(item);
  };

  const isEditing = editingTarget?.originJobKey === item.originJobKey;
  const isCompletingTarget = completingTarget?.originJobKey === item.originJobKey;

  const isOptimisticDraft = !!(item?.__optimistic || item?.isSaving);
  const rawHealth = getTaskHealthDisplay(isOptimisticDraft ? null : item.analytics);
  const health = isOptimisticDraft
    ? {
        ...rawHealth,
        label: 'Scheduling…',
        colorScheme: 'gray',
        severity: 'neutral',
        status: 'pending',
        description: 'Saving this reminder. Analytics will appear after the first reminder fires.',
        microcopy: 'Saving this reminder now — analytics show up after it fires once.',
      }
    : rawHealth;
  const healthIcon = health.severity === 'good'
    ? LuCircleCheck
    : (health.severity === 'warning' || health.severity === 'bad' ? LuTriangleAlert : LuClock);
  const healthScheme = health.colorScheme || 'gray';
  const healthColor = useColorModeValue(`${healthScheme}.800`, `${healthScheme}.200`);
  const accentColors = {
    green: useColorModeValue('green.300', 'green.500'),
    yellow: useColorModeValue('yellow.300', 'yellow.500'),
    orange: useColorModeValue('orange.300', 'orange.500'),
    red: useColorModeValue('red.300', 'red.500'),
    gray: useColorModeValue('gray.300', 'gray.600'),
    blue: useColorModeValue('blue.300', 'blue.500'),
  };
  const statusAccent = accentColors[health.colorScheme] || 'border.subtle';

  // wherever you render the line
  const dayLabel = dayjs(item.nextRunTime).calendar(undefined, {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'dddd',        // "Monday"
    lastDay: '[Yesterday]',
    lastWeek: '[Last] dddd', // "Last Monday"
    sameElse: 'MMM D'        // "Nov 3" for farther dates
  });
  const timeLabel = item?.nextRunTime ? dayjs(item.nextRunTime).format('h:mm A') : null;
  const scheduleLine = [item.naturalCronDescription];
  const scheduleSummary = [dayLabel, scheduleLine].filter(Boolean).join(' • ');
  const completionRatePct = typeof health.completionRatePct === 'number' ? health.completionRatePct : null;
  const healthCopy = health.microcopy || health.description;

  const latestAction = latestActivity?.action?.toLowerCase();
  const latestEventIsControl = latestActivity ? CONTROL_ACTIONS.has(latestAction ?? '') : false;
  const combinedIsPaused = latestActivity
    ? latestAction === 'pause'
    : (!!item?.isPaused || ledgerPaused);

  // Effective firing detection: use server flag if present OR if nextRunTime is now/past (client-side fallback)
  const isDueByTime = item?.nextRunTime ? new Date(item.nextRunTime).getTime() <= Date.now() : false;
  const canUseTemporalFallback = !latestEventIsControl;
  const isFiringEffective = !isOptimisticDraft && !combinedIsPaused && (
    !!firingStatus || (canUseTemporalFallback && isDueByTime)
  );

  const pauseResumeBusy = combinedIsPaused ? isResuming : isPausing;

  return (
    <MotionFlex
      key={item.originJobKey + item.nextRunTime}
      align="flex-start" minW="0" w="100%" maxW="100%"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Removed margin-right on connector Box to reduce left gap */}
      <Box position="relative" mr={1}>
        {/* Connector line can be handled by parent if needed */}
      </Box>
      <Box
          flex="1"
          w="100%"
          maxW="100%"
          p={0}
          borderWidth="1px"
          borderRadius="xl"
          overflow="hidden"
          position="relative"
          cursor="pointer"
          tabIndex={0}
          id={`schedule-${item.originJobKey}`}
          onKeyDown={(e) => { if (e.key?.toLowerCase() === 'e') { handleEditIntent(e); } else if (e.key === 'Escape' && isEditing) { /* let composer handle cancel */ } }}
          data-job={item.originJobKey}
          borderColor={isEditing ? 'purple.400' : (isCompletingTarget ? 'green.400' : 'border.subtle')}
          bg={isEditing || isCompletingTarget ? 'bg.elevated' : 'bg.surface'}
          borderLeftWidth="3px"
          borderLeftColor={statusAccent}
          boxShadow={useColorModeValue('paper', undefined)}
          sx={isEditing ? {
          borderWidth: '2px',
          borderStyle: 'solid',
          borderColor: 'rgba(124,92,255,0.9)',
          borderRadius: '8px',
          boxShadow: '0 0 0 3px rgba(124,92,255,0.08), 0 0 12px rgba(124,92,255,0.12)',
          animation: 'ekGlow 2000ms ease-in-out infinite',
          '@keyframes ekGlow': {
            '0%': { boxShadow: '0 0 0 0 rgba(124,92,255,0.18), 0 0 6px rgba(124,92,255,0.06)' },
            '50%': { boxShadow: '0 0 0 6px rgba(124,92,255,0.06), 0 0 18px rgba(124,92,255,0.12)' },
            '100%': { boxShadow: '0 0 0 0 rgba(124,92,255,0.18), 0 0 6px rgba(124,92,255,0.06)' },
          }
        } : (isCompletingTarget ? {
          borderWidth: '2px',
          borderStyle: 'solid',
          borderColor: 'rgba(72,187,120,0.9)',
          borderRadius: '8px',
          boxShadow: '0 0 0 3px rgba(72,187,120,0.06), 0 0 12px rgba(72,187,120,0.12)',
          animation: 'ekGlowGreen 2000ms ease-in-out infinite',
          '@keyframes ekGlowGreen': {
            '0%': { boxShadow: '0 0 0 0 rgba(72,187,120,0.18), 0 0 6px rgba(72,187,120,0.06)' },
            '50%': { boxShadow: '0 0 0 6px rgba(72,187,120,0.06), 0 0 18px rgba(72,187,120,0.12)' },
            '100%': { boxShadow: '0 0 0 0 rgba(72,187,120,0.18), 0 0 6px rgba(72,187,120,0.06)' },
          }
        } : {})}
      >
        <Box>
          <Box
            px={3}
            py={2}
            bg="bg.subtle"
            borderBottomWidth="1px"
            borderColor="border.subtle"
            onDoubleClick={handleEditIntent}
            onClick={toggleExpand}
            aria-expanded={expanded}
          >
            <Flex
              justify="space-between"
              align={{ base: 'flex-start', sm: 'center' }}
              gap={3}
              flexWrap="wrap"
            >
              <Box flex="1" minW="0" display="flex" flexDirection="column" gap={1}>
                <Flex align="center" gap={2} flexWrap="wrap">
                  <Text
                    fontSize="sm"
                    color={metadataColor}
                    maxW="100%"
                    whiteSpace="normal"
                    overflowWrap="anywhere"
                    wordBreak="break-word"
                    lineHeight="1.3"
                    aria-label={`Schedule: ${scheduleSummary}`}
                    noOfLines={2}
                  >
                    {scheduleSummary}
                  </Text>
                  {combinedIsPaused && (
                    <Badge
                      colorScheme="yellow"
                      borderRadius="full"
                      display="inline-flex"
                      alignItems="center"
                      gap={1}
                      px={2}
                      py={0.5}
                      fontSize="0.7rem"
                    >
                      <LuPause />
                      Paused
                    </Badge>
                  )}
                </Flex>

                {!expanded && (
                  <Text
                    fontSize="md"
                    flexBasis="100%"
                    noOfLines={2}
                    whiteSpace="normal"
                    overflowWrap="break-word"
                    className={isFiringEffective ? "rainbow-text" : ""}
                    color={isFiringEffective ? undefined : 'text.primary'}
                    mt={1}
                  >
                    {item.description}
                  </Text>
                )}
              </Box>
              <Flex
                align="center"
                gap={2}
                flexWrap="wrap"
                justify={{ base: 'flex-start', sm: 'flex-end' }}
                minW="0"
                maxW="100%"
              >
                <Icon
                  as={LuChevronDown}
                  aria-hidden
                  transform={expanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                  transition="transform 0.18s ease"
                  color="text.muted"
                />
                {!isOptimisticDraft && isFiringEffective && (
                  <Button
                    size="sm"
                    variant="outline"
                    height="auto"
                    px={3}
                    py={1}
                    colorScheme="green"
                    leftIcon={<LuCheck />}
                    onClick={(e) => {
                      e.stopPropagation();
                      track('schedule_complete_intent', { source: 'timeline' });
                      // Enter completion mode in the global composer
                      if (typeof window.setGlobalCompletingTarget === 'function') {
                        window.setGlobalCompletingTarget({ originJobKey: item.originJobKey, description: item.description });
                      }
                    }}
                  >
                    <Text color="green.400" fontSize="sm">Complete</Text>
                  </Button>
                )}
                {isOptimisticDraft && (
                  <Badge colorScheme="gray" variant="subtle">Saving…</Badge>
                )}
                {/* Consolidated actions into a single menu to reduce header clutter */}
                <Menu isLazy isOpen={menuOpen} onOpen={openMenu} onClose={closeMenu}>
                  <MenuButton
                    as={IconButton}
                    aria-label="Actions"
                    icon={<Icon as={FiMoreVertical} />}
                    size="sm"
                    variant="ghost"
                    color={menuColor}
                    _hover={{ bg: menuItemHover }}
                    onClick={(e) => { e.stopPropagation(); openMenu(); }}
                  />
                  {/* render menu in portal so it overlays outside of clipped containers */}
                  <Portal>
                    <MenuList
                      onClick={(e) => e.stopPropagation()}
                      bg={useColorModeValue('bg.subtle', menuBg)}
                      color={menuColor}
                      borderColor={menuBorder}
                      boxShadow={menuShadow}
                      zIndex={9999}
                    >
                      <Box
                        as="a"
                        role="menuitem"
                        display="flex"
                        alignItems="center"
                        gap={3}
                        px={3}
                        py={2}
                        cursor="pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          // close the menu first
                          closeMenu();
                          // then enter edit mode
                          handleEditIntent(e);
                          // focus the composer input if available (ChatBar sets #composer-input)
                          setTimeout(() => {
                            const el = document.getElementById('composer-input');
                            if (el) el.focus();
                          }, 60);
                        }}
                        _hover={{ bg: menuItemHover, textDecoration: 'none' }}
                      >
                        <Icon as={FiEdit2} />
                        <Text> Edit</Text>
                      </Box>
                      <Box
                        as="a"
                        role="menuitem"
                        display="flex"
                        alignItems="center"
                        gap={3}
                        px={3}
                        py={2}
                        cursor={pauseResumeBusy ? 'wait' : 'pointer'}
                        opacity={pauseResumeBusy ? 0.6 : 1}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (pauseResumeBusy) return;
                          closeMenu();
                          if (combinedIsPaused) {
                            await handleResume();
                          } else {
                            await handlePause();
                          }
                        }}
                        _hover={{ bg: menuItemHover, textDecoration: 'none' }}
                      >
                        <Icon as={combinedIsPaused ? LuPlay : LuPause} color={combinedIsPaused ? 'green.300' : undefined} />
                        <Text>{combinedIsPaused ? 'Resume reminders' : 'Pause reminders'}</Text>
                        {pauseResumeBusy && <Spinner ml="auto" size="sm" />}
                      </Box>
                      <MenuDivider />
                      <Box
                        as="a"
                        role="menuitem"
                        display="flex"
                        alignItems="center"
                        gap={3}
                        px={3}
                        py={2}
                        cursor="pointer"
                        onClick={(e) => { e.stopPropagation(); closeMenu(); onOpen(); }}
                        _hover={{ bg: menuItemHover, textDecoration: 'none' }}
                      >
                        <Icon as={LuTrash2} color="red.400" />
                        <Text color="red.400"> Delete</Text>
                      </Box>
                    </MenuList>
                  </Portal>
                </Menu>
              </Flex>
            </Flex>
          </Box>
        </Box>
        {expanded && (
          <Flex direction="column" px={3} py={3} bg="bg.surface">
            <Flex align="center" gap={2} flexWrap="wrap" mb={2}>
              <Icon as={healthIcon} color={healthColor} />
              <Text fontWeight="semibold" fontSize="sm" color={healthColor} whiteSpace="nowrap">
                {health.label}.
              </Text>
              {healthCopy && (
                <Text fontSize="sm" color={healthColor} whiteSpace="normal" overflowWrap="anywhere">
                  {healthCopy}
                </Text>
              )}
            </Flex>
            <Text fontSize="sm" color="text.muted" display="flex" alignItems="center" gap="1" flexShrink={0} mb={2}>
              <LuClock />
              {countdown}
            </Text>
            <Box flex="1" pr={4}>
              <Flex
                fontSize="sm"
                color={metadataColor}
                align="center"
                gap={1}
                flexWrap="wrap"
                mt={1}
              >
              </Flex>
              {activityData && getConsecutiveRetryCount() > 0 && (
                <Text fontSize="sm"
                  color="orange.400">
                  {getConsecutiveRetryCount()} missed reminders
                </Text>
              )}
              <Text
                fontSize="xl"
                fontWeight="semibold"
                lineHeight="1.2"
                mt={1}
                className={isFiringEffective ? "rainbow-text" : ""}
              >
                {item.description}.
              </Text>
              {/* <Text fontSize="xs" color="gray.400" mt={1}>
                You added this on {new Date(item.createdDate).toLocaleString()}
              </Text> */}
            </Box>
          </Flex>
        )}
        <AnimatePresence initial={false}>
          {expanded && activityData && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <Box mt={4} width="100%" maxHeight="400px" overflowY="auto">
                <ScheduleTimelineDetails activityData={activityData} />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
        <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
          <AlertDialogOverlay>
            <AlertDialogContent bg={modalContentBg} color={modalTextColor}>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete
              </AlertDialogHeader>
              <AlertDialogBody>
                {/* <Text mb={3}>You are about to delete the following scheduled item:</Text> */}
                <Box borderWidth="1px" borderRadius="md" p={3} mb={3} bg={dialogBoxBg}>
                  <Text fontWeight="bold" fontSize="md" mb={2}>{item.description}</Text>
                  <Text fontSize="sm" color={dialogMuted} mb={1}>
                    Created on: {new Date(item.createdDate).toLocaleString()}
                  </Text>
                  <Text fontSize="sm" color={dialogMuted}>
                    Next run: {dayjs(item.nextRunTime).calendar()}
                  </Text>
                </Box>
                <Flex align="center" color={dialogMuted} mb={2}>
                  <Text fontSize="sm">
                    Deleting this will stop all future notifications for this item and remove it from your rotation.
                  </Text>
                </Flex>
                <Text color="text.muted" fontSize="sm">
                  Your activity history for this item will remain available.
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleDelete}
                  ml={3}
                  isLoading={deletingJob === item.originJobKey}
                >
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>
    </MotionFlex>
  )
};

export default ScheduleItem;
